import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Check, Sparkles, Image, Upload, Star, ArrowRight, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const TIER_ICONS = { basic: Star, pro: Sparkles, elite: Crown };
const TIER_COLORS = { basic: "border-white/20", pro: "border-gold/50 bg-gold/[0.03]", elite: "border-gold bg-gold/[0.06]" };

export default function SubscribePage() {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState("pro");
  const subSuccess = searchParams.get("sub");

  useEffect(() => {
    axios.get(`${API}/subscriptions/plans`).then(r => setPlans(r.data)).catch(() => {});
  }, []);

  const subscribe = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email required."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscriptions/checkout`, { tier_id: selectedTier, name, email, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed.");
    } finally { setLoading(false); }
  };

  if (subSuccess) {
    return (
      <div data-testid="subscribe-success" className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-12 text-center max-w-md">
          <CheckCircle size={48} className="text-gold mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-white mb-3">Welcome to the Club</h1>
          <p className="text-white/50 font-body text-sm mb-6">Your subscription is being activated. You'll receive confirmation shortly.</p>
          <a href="/" className="inline-flex items-center gap-2 bg-gold text-black font-body text-sm px-6 py-3 hover:bg-gold-light transition-colors">Explore <ArrowRight size={14} /></a>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="subscribe-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="text-center mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-4">Membership</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl font-light text-white mb-4">
              Premium <span className="gold-gradient-text">Subscriptions</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body text-base max-w-xl mx-auto">
              Access exclusive content, AI photo enhancement, and VIP perks. Choose the tier that fits your lifestyle.
            </motion.p>
          </motion.div>

          {/* Pricing Cards */}
          <motion.div initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {plans.map((plan, i) => {
              const Icon = TIER_ICONS[plan.id] || Star;
              const isSelected = selectedTier === plan.id;
              const isPopular = plan.id === "pro";
              return (
                <motion.div
                  key={plan.id}
                  variants={fadeUp}
                  custom={i + 3}
                  onClick={() => setSelectedTier(plan.id)}
                  data-testid={`plan-${plan.id}`}
                  className={`relative glass-card p-8 cursor-pointer transition-all border-2 ${isSelected ? "border-gold scale-[1.02]" : TIER_COLORS[plan.id]} hover:border-gold/50`}
                >
                  {isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gold text-black text-[10px] font-body font-bold uppercase tracking-wider">Most Popular</div>}
                  <div className="text-center mb-6">
                    <Icon size={28} className="text-gold mx-auto mb-3" />
                    <h3 className="font-heading text-xl text-white mb-1">{plan.name}</h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-gold font-heading text-4xl">${plan.price}</span>
                      <span className="text-white/30 font-body text-sm">/{plan.interval}</span>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-white/60 text-xs font-body">
                        <Check size={14} className="text-gold flex-shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Subscribe Form */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={6} className="max-w-md mx-auto glass-card p-8 space-y-5" data-testid="subscribe-form">
            <h3 className="font-heading text-lg text-white text-center">Start Your {SUBSCRIPTION_TIERS_NAMES[selectedTier] || "Pro"} Membership</h3>
            <div className="space-y-4">
              <Input data-testid="sub-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
              <Input data-testid="sub-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
            </div>
            <button onClick={subscribe} disabled={loading} data-testid="sub-submit" className="w-full bg-gold text-black font-body font-medium text-sm py-4 hover:bg-gold-light transition-colors disabled:opacity-50">
              {loading ? "Processing..." : `Subscribe — $${plans.find(p => p.id === selectedTier)?.price || "29.99"}/month`}
            </button>
            <p className="text-white/20 text-[10px] font-body text-center">Secure payment via Stripe. Cancel anytime.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

const SUBSCRIPTION_TIERS_NAMES = { basic: "Basic", pro: "Pro", elite: "Elite" };
